// Copyright (c) Docugami, Inc. All rights reserved.

import { DocumentStatus, TokenRange } from "./api";
import { LabelSchema } from "store/frontend/frontendTypes";

// Moved here from reportsSlice.ts to break circular dependency
export interface OperationMetadata {
    folderId?: string;
    entryId?: string;
}

export enum BindingProviderId {
    System = 'System',
    User = 'User',
    AutoComplete = 'AutoComplete',
    XPath = 'XPath',
    LiveQA = 'LiveQA',
    OneClick = 'OneClick',
    ZeroShotLiveQA = 'ZeroShotLiveQA',
    LlmQA = 'LlmQA',
    LlmQARed = 'LlmQARed',
    Chat = 'Chat',
    Transform = 'Transform'
}

export enum ReportEntryType {
    Text = 'Text'
}

export enum DocumentEvaluationStatus {
    None = 'None',
    Confirmed = 'Confirmed',
    Published = 'Published',
    OutOfDate = 'OutOfDate',
    Error = 'Error'
}

export enum ReportViewContext {
    Published = 'Published',
    Review = 'Review'
}

export interface BaseMutationResponse {
    operationId: string;
    streamResponse: StreamResponse;
}

export interface ReportDefinition {
    id: string;
    name: string;
    clusterId: string;
    entries: string[];
    isPublished: boolean;
    autoPublishDocuments: boolean;
    version: number;
    activeBatchId: string;
};

export interface ReportEntry {
    id: string;
    reportId: string;
    title: string;
    labelId: string;
    entryType: ReportEntryType;
    version: number;
    groupId?: string;
}

export interface ReportEntryGroup {
    id: string;
    reportId: string;
    label: string;
    entries: string[];
    version: number;
}

export interface ReportEntryComplexType {
    id: string;
    reportId: string;
    label: string;
    labelId: string;
    repeats: boolean;
    entries: string[];
    version: number;
}

export type BindingMetadata = { [key: string]: string; }

export interface ReportBinding {
    id: string;
    entryId: string;
    folderId: string;
    userConfirmed: boolean;
    isConfirmed: boolean;
    bindingProviderId: BindingProviderId;
    spanId?: string;
    complexTypeInstanceId?: string;
    metadata?: BindingMetadata;
}

export type ReportBindingEvaluationPreview = ReportBindingEvaluation;

export interface ReportBindingEvaluation {
    id: string;
    fileId: string;
    dgmlVersion?: string;
    spanId?: string;
    richTextValue?: string;
    tokenRange?: TokenRange;
    isPreview?: boolean;
}

export interface ReportDocumentState {
    index: number;
    folderId: string;
    evaluationStatus: DocumentEvaluationStatus;
    dgmlVersion?: string;
    errorMessage?: string;
    // Document metadata from the cluster document
    filename: string;
    status: DocumentStatus;
    hasCpml: boolean;
}

export interface GetReportSchemaResponse extends BaseQueryResponse {
    report: ReportDefinition;
    entries: ReportEntry[];
    groups: ReportEntryGroup[];
    complexTypes: ReportEntryComplexType[];
}

export interface EntryBindings {
    entryId: string;
    folderId: string;
    bindings: ReportBinding[];
    suggestions?: ReportBinding[];
}

export interface GetBindingsResponse {
    entryBindings: EntryBindings[];
    version: string;
}

export interface GetReportBindingEvaluationsResponse {
    evaluations: ReportBindingEvaluationPreview[];
    version: string;
}

export interface GetReportBindingEvaluationResponse {
    evaluation: ReportBindingEvaluation;
}

export interface GetReportDocumentsResponse extends BaseQueryResponse {
    docsetReadyDocumentCount: number;
    documents: ReportDocumentState[];
}

export interface FeedbackLocks {
    [id: string]: boolean;
}

export type DocumentFeedbackLocks = {
    [id: string]: FeedbackLocks
}

export interface ReviewFeedbackLockStats {
    documentsAllLocked: FeedbackLocks;
    entriesAllLocked: FeedbackLocks
    lockStats: DocumentFeedbackLocks
}

export interface BindEntryResponse extends BaseMutationResponse {
    docsetId: string;
    entryId: string;
    label?: LabelSchema;
}

export interface StreamResponse {
    version: number;
    changes: ReportStreamChangeNotification
}

export interface BaseRequest {
    operationId: string;
}

export interface RenameReportRequest extends BaseRequest {
    name: string;
}

export interface SetReportEntriesOrderRequest extends BaseRequest {
    entryIds: string[];
}

export interface ReportSettings {
    autoPublishDocuments: boolean;
    exportAbstract: boolean;
    createDefaultAbstractTemplate: boolean;
}

export interface PublishReportRequest extends BaseRequest {
    folderIds: string[],
    reportSettings: ReportSettings;
}

export interface AbstractTemplateUploadRequest {
    formData: FormData;
}

export interface UnpublishReportRequest extends BaseRequest {
}

export interface UpdateReportSettingsRequest extends BaseRequest {
    reportSettings: ReportSettings;
}

export interface ConfirmDocumentsRequest extends BaseRequest {
    folderIds: string[];
}

export interface ClientRequest<T extends BaseRequest> {
    request: T;
    operationMetadata?: OperationMetadata;
}

export interface CreateEntryDefinition {
    title: string;
    entryType: ReportEntryType;
    index?: number;
    groupId?: string;
}

export enum BindEntryAction {
    Add = 'Add',
    Remove = 'Remove'
}

export interface BulkConfirmBindingsRequest extends BaseRequest {
    labelledSpans: LabelledSpan[];
}

export interface LabelledSpan {
    fileId: string;
    labelId: string;
    spanId?: string;
}

export interface BindEntryRequest extends BaseRequest {
    createEntryDefinition?: CreateEntryDefinition;
    entryId?: string;
    action: BindEntryAction;
    folderId: string;
    spanId?: string;
    dgmlVersion?: string;
    previousSpanId?: string;
    complexTypeInstanceId?: string;
}

export interface SetEntryBindingsRequest extends BaseRequest {
    entryId: string;
    folderId: string;
    replace: boolean;
    spanIds?: string[];
    dgmlVersion?: string;
}

export interface AddLabelRequest extends BaseRequest {
    label: string;
    labelId?: string;
    index?: number;
    groupId?: string;
}

export interface AddLabelResponse extends BaseMutationResponse {
    id: string;
    labelId: string;
    entryIds: string[];
}

export interface AddEntryRequest extends BaseRequest {
    definition: CreateEntryDefinition;
}

export interface AddEntryResponse extends BaseMutationResponse {
    entryId: string;
}

export interface LabelReportEntryRequest extends BaseRequest {
    label: string;
}

export interface LabelReportEntryResponse extends BaseMutationResponse {
}

export interface BaseEntryBindingsRequest extends BaseRequest {
    entryId: string;
    folderId: string;
}

export interface FlagEntryBindingRequest extends BaseEntryBindingsRequest {
    spanId?: string;
}

export interface GetReportGroupsResponse extends BaseQueryResponse {
    groups: ReportEntryGroup[];
}

export interface AddComplexTypEntryRequest extends BaseRequest {
    label: string;
    labelId?: string;
    entries?: string[];
    orderedEntries?: string[];
}

export interface AddComplexTypeEntryResponse extends BaseMutationResponse {
    complexTypeEntryId: string;
}

export interface ModifyComplexTypeEntries extends BaseRequest {
    complexTypeId: string;
    entries: string[];
    orderedEntries?: string[];
}

export interface AddGroupRequest extends BaseRequest {
    label: string;
    entries?: string[];
    orderedEntries?: string[];
}

export interface AddGroupResponse extends BaseMutationResponse {
    groupId: string;
}

export interface BaseGroupRequest extends BaseRequest {
    groupId: string;
}

export interface LabelGroupRequest extends BaseGroupRequest {
    label: string;
}

export interface ModifyGroupEntriesRequest extends BaseGroupRequest {
    entries: string[];
    orderedEntries?: string[];
}

export interface ReportOperationProcessedMessage {
    reportId: string;
    operationId: string;
    version: number;
    succeeded: boolean;
    changes: ReportStreamChangeNotification | null;
}

export interface ReportStreamChangeNotification {
    schemaModified?: boolean;
    bindingsModified?: boolean;
    userModifiedBindings?: boolean;
    activeBatchBindingsModified?: boolean;
    publishedBindingsModified?: boolean;
    reviewDocumentStateModified?: boolean;
    publishedDocumentStateModified?: boolean;
    batchChanged?: boolean;
    groupsModified?: boolean;
    publishChanged?: boolean;
    deleted?: boolean;
    modifiedBindingsDocumentIds?: string[];
    outOfDateDocumentIds?: string[];
    entriesAdded?: boolean;
}

export interface ReportEvaluationUpdated {
    reportId: string;
    timestamp: number;
    updated: EvaluationId[];
    reviewBatch: boolean;
}

export interface EvaluationId {
    folderId: string;
    entryId: string;
}

export interface IndexChangedMessage {
    indexName: string;
    docsetId: string;
    fileId: string;
    timestampUtcMs: number;
}

export interface LabelValuesUpdatedMessage {
    docsetId: string;
    fileId: string;
    labelIds: string[];
    timestampUtcMs: number;
}

export interface BaseQueryResponse {
    version: number;
}

export interface GetPublishedAbstractStatusRequest {
    folderId: string;
}

export interface FlaggedResponse extends BaseQueryResponse {
    documents: FlaggedFile[];
    entries: string[];
    instances: FlagInstance[];
}

export interface FlaggedFile {
    folderId: string;
    filePath: string;
}

export interface FlagInstance {
    folderId: string;
    entryId: string;
}

export const bindingProviderPriorityLookup: Record<BindingProviderId, number> = {
    [BindingProviderId.User]: 10,
    [BindingProviderId.Transform]: 9,
    [BindingProviderId.LlmQARed]: 8,
    [BindingProviderId.LiveQA]: 7,
    [BindingProviderId.LlmQA]: 6,
    [BindingProviderId.OneClick]: 5,
    [BindingProviderId.AutoComplete]: 4,
    [BindingProviderId.Chat]: 3,
    [BindingProviderId.ZeroShotLiveQA]: 2,
    [BindingProviderId.XPath]: 1,
    [BindingProviderId.System]: 0
}

export interface BulkClearBindingsRequest extends BaseRequest {
    folderId: string;
    entryId: string;
    spanIds: string[];
}
